import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// This is a base store to be combined with specific feature stores
// It provides URL synchronization capabilities

type UrlSyncOptions = {
  serialize?: (value: any) => string;
  deserialize?: (value: string) => any;
  skipSync?: boolean;
}

interface UrlStateStore {
  // Methods for syncing with URL
  syncFromUrl: (searchParams: URLSearchParams) => void;
  syncToUrl: (searchParams: URLSearchParams) => URLSearchParams;
  
  // Store for URL sync configuration
  urlSyncConfig: Record<string, UrlSyncOptions>;
  setUrlSyncConfig: (key: string, options: UrlSyncOptions) => void;
  
  // Store for all URL synced values
  urlValues: Record<string, any>;
  setUrlValue: <T>(key: string, value: T) => void;
}

export const useUrlStateStore = create<UrlStateStore>((set, get) => ({
  urlSyncConfig: {},
  urlValues: {},
  
  setUrlSyncConfig: (key, options) => {
    // Check if the config already exists and is the same
    const currentConfig = get().urlSyncConfig[key];
    if (currentConfig && 
        currentConfig.skipSync === options.skipSync && 
        currentConfig.serialize === options.serialize && 
        currentConfig.deserialize === options.deserialize) {
      return; // Skip update if nothing changed
    }
    
    set(state => ({
      urlSyncConfig: {
        ...state.urlSyncConfig,
        [key]: options
      }
    }));
  },
  
  setUrlValue: (key, value) => {
    set(state => {
      // Skip update if the value hasn't changed
      if (state.urlValues[key] === value) {
        return state;
      }
      
      return {
        urlValues: {
          ...state.urlValues,
          [key]: value
        }
      };
    });
  },
  
  syncFromUrl: (searchParams) => {
    const { urlSyncConfig, urlValues } = get();
    const newValues: Record<string, any> = {};
    let hasChanges = false;
    
    // Process each key in the URL sync config
    Object.entries(urlSyncConfig).forEach(([key, options]) => {
      if (options.skipSync) return;
      
      if (searchParams.has(key)) {
        const rawValue = searchParams.get(key) || '';
        const deserialize = options.deserialize || defaultDeserialize;
        const newValue = deserialize(rawValue);
        
        // Only update if the value has changed
        if (urlValues[key] !== newValue) {
          newValues[key] = newValue;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      set(state => ({
        urlValues: {
          ...state.urlValues,
          ...newValues
        }
      }));
    }
  },
  
  syncToUrl: (searchParams) => {
    const { urlSyncConfig, urlValues } = get();
    
    // Process each key in the URL sync config
    Object.entries(urlSyncConfig).forEach(([key, options]) => {
      if (options.skipSync) return;
      
      const value = urlValues[key];
      if (value === undefined || value === null) {
        if (searchParams.has(key)) {
          searchParams.delete(key);
        }
      } else {
        const serialize = options.serialize || defaultSerialize;
        const serialized = serialize(value);
        
        if (serialized !== searchParams.get(key)) {
          searchParams.set(key, serialized);
        }
      }
    });
    
    return searchParams;
  }
}));

// Default serializer
function defaultSerialize(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  
  if (Array.isArray(value)) {
    return value.join(',');
  }
  
  return JSON.stringify(value);
}

// Default deserializer
function defaultDeserialize(value: string): any {
  // Try to parse as JSON
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      return JSON.parse(value);
    } catch (e) {
      // If JSON parsing fails, fall through to other cases
    }
  }
  
  // Parse boolean values
  if (value === 'true') return true;
  if (value === 'false') return false;
  
  // Parse numeric values
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    const num = Number(value);
    if (!isNaN(num)) return num;
  }
  
  // Parse comma-separated arrays
  if (value.includes(',')) {
    const parts = value.split(',').map(part => part.trim());
    // Try to convert array items to proper types
    const typedParts = parts.map(part => {
      if (part === 'true') return true;
      if (part === 'false') return false;
      if (/^-?\d+(\.\d+)?$/.test(part)) {
        const num = Number(part);
        if (!isNaN(num)) return num;
      }
      return part;
    });
    return typedParts;
  }
  
  // Return as is for other cases
  return value;
}

// Hook to sync URL parameters with the store
export function useUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { syncFromUrl, syncToUrl } = useUrlStateStore();
  
  // Sync from URL to store when URL changes
  useEffect(() => {
    syncFromUrl(searchParams);
  }, [searchParams]);
  
  // Function to sync from store to URL
  const updateUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const updatedParams = syncToUrl(newSearchParams);
    
    // Compare if the parameters have changed
    const oldParamsString = searchParams.toString();
    const newParamsString = updatedParams.toString();
    
    if (oldParamsString !== newParamsString) {
      setSearchParams(updatedParams);
    }
  }, [searchParams, setSearchParams, syncToUrl]);
  
  return { updateUrl };
}

// Hook to access and update a specific URL value
export function useUrlValue<T>(key: string, defaultValue?: T, options: UrlSyncOptions = {}) {
  const store = useUrlStateStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const optionsRef = useRef(options);
  const defaultValueRef = useRef(defaultValue);
  const isInitializedRef = useRef(false);

  // Attempt to read initial value directly from URL for the first render.
  // This is a synchronous check.
  let initialValue: T | undefined;
  if (searchParams.has(key) && !optionsRef.current.skipSync) {
    const rawValue = searchParams.get(key) || '';
    const deserialize = optionsRef.current.deserialize || defaultDeserialize;
    try {
      initialValue = deserialize(rawValue) as T;
    } catch (e) {
      console.error(`Error deserializing URL parameter ${key} during initial read:`, e);
      // If deserialization fails, proceed as if param wasn't there or rely on store/default
      initialValue = store.urlValues[key] ?? defaultValueRef.current;
    }
  } else {
    // If not in URL, check store, then default
    initialValue = store.urlValues[key] ?? defaultValueRef.current;
  }

  // The value from the store, falling back to our determined initialValue if not yet in store.
  // This useShallow is crucial for Zustand subscriptions.
  const valueFromStore = useUrlStateStore(useShallow(state => state.urlValues[key]));
  const value = valueFromStore === undefined ? initialValue : valueFromStore;
  
  const valueRef = useRef(value);

  // Keep options ref updated
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Update the ref when the value changes (either from store or initial)
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  
  // Update the default value ref when it changes
  useEffect(() => {
    defaultValueRef.current = defaultValue;
  }, [defaultValue]);

  // Effect to register the key for URL syncing and initialize/update the store.urlValues[key]
  useEffect(() => {
    // Always ensure the config is set.
    // It's okay to call this multiple times if options change, setUrlSyncConfig handles idempotency.
    store.setUrlSyncConfig(key, optionsRef.current);

    if (!isInitializedRef.current) {
      // On first initialization for this hook instance:
      // Prioritize URL param, then default value for setting the store.urlValues[key]
      // This ensures the global store (urlValues) is also aligned.
      let valueToSetInStore: T | undefined;
      let valueSet = false;

      if (searchParams.has(key) && !optionsRef.current.skipSync) {
        const rawValue = searchParams.get(key) || '';
        const deserialize = optionsRef.current.deserialize || defaultDeserialize;
        try {
          valueToSetInStore = deserialize(rawValue) as T;
          valueSet = true;
        } catch (e) {
          console.error(`Error deserializing URL parameter ${key} in effect:`, e);
          // Fallback to default if deserialization error
        }
      }
      
      if (!valueSet && defaultValueRef.current !== undefined) {
        valueToSetInStore = defaultValueRef.current;
        valueSet = true;
      }

      // Only set in store if we determined a value and it's different or not yet set
      if (valueSet && store.urlValues[key] !== valueToSetInStore) {
        store.setUrlValue(key, valueToSetInStore);
      }
      isInitializedRef.current = true;
    }
  }, [key, store, searchParams, defaultValue, options]); // options are needed here if skipSync can change

  const setValue = useCallback((newValue: T) => {
    if (newValue === valueRef.current && store.urlValues[key] === newValue) return;

    store.setUrlValue(key, newValue); // Update the central store

    if (optionsRef.current.skipSync) return;

    // Update URL
    // Create a new URLSearchParams instance from current searchParams
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const serialize = optionsRef.current.serialize || defaultSerialize;
    const serializedValue = serialize(newValue);

    if (newValue === undefined || newValue === null || serializedValue === '') {
      if (newSearchParams.has(key)) {
        newSearchParams.delete(key);
      }
    } else {
      if (newSearchParams.get(key) !== serializedValue) {
        newSearchParams.set(key, serializedValue);
      }
    }
    
    if (searchParams.toString() !== newSearchParams.toString()) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [key, searchParams, setSearchParams, store]); // valueRef removed from deps, rely on store directly or passed newValue

  return [value, setValue] as const;
} 